import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCurrentOrganization } from 'utils/util/hooks';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import {
  TemplateEditorFormObject,
  TemplateTypeConfigs,
} from './templateTypeConfigs';
import {
  ContentTemplate,
  ContentTemplateMutationResponse,
  TemplateType,
} from './types';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  template: ContentTemplate | null;
  defaultType: TemplateType;
  // everything type-specific (fields, validation, payload) is injected; the
  // type dropdown offers exactly the configured types
  typeConfigs: TemplateTypeConfigs;
  onSaved: (template: ContentTemplateMutationResponse) => void;
}

const toFormObject = (
  template: ContentTemplate | null,
  defaultType: TemplateType,
  typeConfigs: TemplateTypeConfigs,
  organizationId?: number
): TemplateEditorFormObject => {
  const base: TemplateEditorFormObject = {
    // every configured type's defaults are present so switching type in the
    // dropdown always starts from a clean form
    ...Object.values(typeConfigs).reduce(
      (values, config) => ({ ...values, ...config.emptyFormValues }),
      {}
    ),
    organization: organizationId,
    type: template?.type ?? defaultType,
    name: template?.name ?? '',
  };
  if (!template) {
    return base;
  }
  return {
    ...base,
    ...(typeConfigs[template.type]?.fromContent(template.content) ?? {}),
  };
};

const TemplateEditorModal: FC<Props> = ({
  isOpen,
  toggle,
  template,
  defaultType,
  typeConfigs,
  onSaved,
}) => {
  const { formatMessage } = useIntl();
  const currentOrganization = useCurrentOrganization();

  const [formObj, setFormObj] = useState<TemplateEditorFormObject>(() =>
    toFormObject(template, defaultType, typeConfigs, currentOrganization?.id)
  );

  useEffect(() => {
    if (isOpen) {
      setFormObj(
        toFormObject(
          template,
          defaultType,
          typeConfigs,
          currentOrganization?.id
        )
      );
    }
  }, [isOpen, template, defaultType, typeConfigs, currentOrganization?.id]);

  const handleChange = useCallback((obj: TemplateEditorFormObject) => {
    setFormObj(obj);
  }, []);

  const inputs = useMemo(
    () => [
      {
        name: 'name',
        type: INPUT_TYPES.TEXT,
        required: true,
        autoFocus: !template,
        autoComplete: 'off',
        ignorePasswordManagers: true,
        maxLength: 255,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.form.name',
          defaultMessage: 'Name',
        }),
      },
      ...(typeConfigs[formObj.type]?.inputs ?? []),
    ],
    [formObj.type, formatMessage, template, typeConfigs]
  );

  const onValidate = useCallback(
    (obj: TemplateEditorFormObject) => {
      const errors: Record<string, string> = {};
      if (!obj.name?.trim()) {
        errors.name = formatMessage({
          id: 'app.views.administration.content_administration.templates.form.error.name_required',
          defaultMessage: 'Enter a name.',
        });
      }
      return { ...errors, ...(typeConfigs[obj.type]?.validate(obj) ?? {}) };
    },
    [formatMessage, typeConfigs]
  );

  const transformObjectBeforeSubmit = useCallback(
    (obj: TemplateEditorFormObject) => ({
      organization: obj.organization,
      // type is immutable once created
      ...(template ? {} : { type: obj.type }),
      name: obj.name.trim(),
      content: typeConfigs[obj.type]?.toContent(obj) ?? {},
    }),
    [template, typeConfigs]
  );

  const callback = useCallback(
    (data: ContentTemplateMutationResponse | null, error, hardError) => {
      if (data && !error && !hardError) {
        onSaved(data);
      }
    },
    [onSaved]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      title={
        template ? (
          <FormattedMessage
            id="app.views.administration.content_administration.templates.editor.title.edit"
            defaultMessage="Edit template"
          />
        ) : (
          <FormattedMessage
            id="app.views.administration.content_administration.templates.editor.title.create"
            defaultMessage="Create template"
          />
        )
      }
      submitText={formatMessage({
        id: 'app.views.administration.content_administration.templates.editor.submit',
        defaultMessage: 'Save template',
      })}
      method={template ? 'PATCH' : 'POST'}
      url={template ? `content-templates/${template.id}` : 'content-templates'}
      object={formObj}
      inputs={inputs}
      onChange={handleChange}
      onValidate={onValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      callback={callback}
      disableUnsavedChangesPrompt={true}
    />
  );
};

export default React.memo(TemplateEditorModal);
